// PopupComponent.ts
import DOMPurify from "dompurify";
import { ensureMaterialSymbols } from "../utils/icons";
import { freezeWidgetLocale } from "../utils/locale";
import { isCompactMobileLayout } from "../utils/presentationOptions";
import { normalizeLauncherOptions } from "./launcherLayout";
import { normalizeWidgetStyling } from "./widgetStyling";
const DEFAULT_WIDGET_BOX_SHADOW = '0 2px 8px rgba(15, 23, 42, 0.12), 0 16px 32px rgba(15, 23, 42, 0.22)';
const DEFAULT_AUTO_CLOSE_DELAY_MS = 2000;
const MOBILE_POPUP_SEEN_PREFIX = 'shine-popup-mobile-seen';
const DEFAULT_Z_INDEX = 3001;
const MIN_Z_INDEX = 0;
const MAX_Z_INDEX = 2147483647;
export class PopupComponent {
    config;
    popupWrapper;
    popup;
    closeButton;
    aiLogo;
    textElement = null;
    isLogo1 = true;
    launcherImageReady = false;
    launcherImageFailed = false;
    logoSwapTimer;
    modal = null;
    closeModalButton = null;
    modalListenerTargets = new WeakSet();
    closeButtonListenerTargets = new WeakSet();
    preloadInProgress = false;
    widgetReadyPromise = null;
    widgetRuntimeMounted = false;
    isPopupClosed = false;
    scrollRevealTriggered = false;
    scrollRevealLoggedListening = false;
    scrollRevealLoggedThreshold = false;
    currentPrompt = null;
    clickReporter = null;
    ensureWidgetRuntime;
    mountTarget;
    inlineLauncher;
    constructor(config, options = {}) {
        this.config = config;
        this.ensureWidgetRuntime = options.ensureWidgetRuntime;
        this.mountTarget = options.mountTarget ?? document.body;
        this.inlineLauncher = options.inlineLauncher === true;
        this.currentPrompt = config.dynamicPrompt?.prompt ?? null;
        this.config.colors = {
            backgroundColor: this.safeCssColor(config.colors?.backgroundColor, '#ffffff'),
            textColor: this.safeCssColor(config.colors?.textColor, '#111827'),
            closeButtonBackground: this.safeCssColor(config.colors?.closeButtonBackground, '#ffffff'),
            closeButtonColor: this.safeCssColor(config.colors?.closeButtonColor, '#111827'),
            closeButtonShadow: this.safeCssShadow(config.colors?.closeButtonShadow, '0 1px 6px 0 rgba(0, 0, 0, 0.06), 0 2px 32px 0 rgb(162 162 162 / 16%)'),
            popupShadow: this.safeCssShadow(config.colors?.popupShadow, DEFAULT_WIDGET_BOX_SHADOW),
            triangleShadow: this.safeCssShadow(config.colors?.triangleShadow, 'none'),
            launcherBackground: this.safeCssColor(config.colors?.launcherBackground, 'transparent'),
            launcherShadow: this.safeCssShadow(config.colors?.launcherShadow, '0 1px 6px 0 rgba(0, 0, 0, 0.06), 0 2px 32px 0 rgb(162 162 162 / 16%)'),
        };
        this.config.images = {
            aiLogo1: this.safeImageUrl(config.images?.aiLogo1),
            aiLogo2: this.safeImageUrl(config.images?.aiLogo2),
            logoSwapEnabled: config.images?.logoSwapEnabled === true,
            launcherLogoPadding: this.safeCssLength(config.images?.launcherLogoPadding, '0px'),
        };
        this.config.text = {
            aiMessage: `${config.text?.aiMessage ?? ''}`,
        };
        this.config.launcherShape = this.normalizeLauncherShape(config.launcherShape);
        this.config.launcher = normalizeLauncherOptions(config.launcher);
        this.config.styling = normalizeWidgetStyling(config.styling);
        this.config.zIndex = this.normalizeZIndex(config.zIndex);
        this.config.popupTailEnabled = config.popupTailEnabled !== false;
        this.config.autoClose = this.normalizeAutoClose(config.autoClose);
        if (typeof this.config.images.logoSwapEnabled !== 'boolean') {
            this.config.images.logoSwapEnabled = false;
        }
        this.popupWrapper = document.createElement('div');
        this.popup = document.createElement('div');
        this.closeButton = document.createElement('button');
        this.aiLogo = document.createElement('button');
        this.aiLogo.type = 'button';
        this.aiLogo.setAttribute('aria-label', options.launcherLabel?.trim() || 'Open chat');
        this.refreshWidgetRefs();
        this.initialize();
    }
    getWidgetRoot() {
        const widget = document.querySelector('shine-widget-component');
        return widget?.shadowRoot ?? document;
    }
    refreshWidgetRefs() {
        const widgetRoot = this.getWidgetRoot();
        this.modal = widgetRoot.querySelector('#shineModal');
        this.closeModalButton = widgetRoot.querySelector('#close-modal-button');
        if (this.closeModalButton && !this.closeButtonListenerTargets.has(this.closeModalButton)) {
            this.closeButtonListenerTargets.add(this.closeModalButton);
            this.closeModalButton.addEventListener('click', () => this.closeModal());
        }
        if (this.modal && !this.modalListenerTargets.has(this.modal)) {
            this.modalListenerTargets.add(this.modal);
            this.modal.addEventListener('mousedown', (event) => {
                if (event.target === this.modal) {
                    this.closeModal();
                }
            });
        }
    }
    initialize() {
        this.popupWrapper.classList.toggle('shine-popup-compact-mobile', this.isCompactMobileLayout());
        this.popupWrapper.classList.toggle('shine-popup-no-tail', this.config.popupTailEnabled === false);
        this.setupPopupWrapper();
        if (!this.inlineLauncher) {
            this.setupPopup();
            this.setupAIIcon();
            this.setupText();
            this.setupCloseButton();
        }
        this.setupAILogo();
        this.applyLauncherLayout();
        if (this.config.launcher || this.hasPopupSizing()) {
            window.addEventListener('resize', () => {
                const suppressed = this.popupWrapper.getAttribute('data-shine-modal-open') === 'true';
                this.setupPopupWrapper();
                this.applyLauncherLayout();
                if (suppressed)
                    this.setLauncherSuppressed(true);
                else
                    this.constrainFloatingPosition();
            });
        }
        this.appendElements();
        this.addEventListeners();
        if (this.config.images.logoSwapEnabled && this.config.images.aiLogo2?.trim()) {
            this.swapImages();
        }
        this.applyStyles();
        this.constrainFloatingPosition();
        if (!this.inlineLauncher) {
            this.handleInitialDisplay();
            this.setupScrollReveal();
        }
    }
    isCompactMobileLayout() {
        return isCompactMobileLayout(this.config.layoutPreset);
    }
    normalizeLauncherShape(value) {
        return `${value ?? ''}`.trim().toLowerCase() === 'rectangle' ? 'rectangle' : 'circle';
    }
    normalizeZIndex(value) {
        return typeof value === 'number'
            && Number.isInteger(value)
            && value >= MIN_Z_INDEX
            && value <= MAX_Z_INDEX
            ? value
            : DEFAULT_Z_INDEX;
    }
    setupPopupWrapper() {
        this.popupWrapper.classList.add('popup-wrapper');
        if (this.inlineLauncher) {
            this.popupWrapper.classList.add('shine-inline-launcher');
            Object.assign(this.popupWrapper.style, {
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                width: 'fit-content',
                maxWidth: '100%',
                boxSizing: 'border-box',
                zIndex: String(this.config.zIndex)
            });
            return;
        }
        const mediaQuery = window.matchMedia('(max-width: 768px)');
        const positionMode = this.getPositionMode();
        const isSticky = positionMode === 'sticky';
        const desktopOffset = '30px';
        const mobileOffset = '15px';
        const currentOffset = mediaQuery.matches ? mobileOffset : desktopOffset;
        const viewport = this.getLauncherViewport();
        const compactMobile = this.isCompactMobileLayout();
        // Leave room for the teaser and its close control when the requested offset is large.
        const maximumRight = Math.max(0, window.innerWidth - (compactMobile ? 96 + 16 : 128 + 25) - 16);
        const rightPx = viewport?.rightPx === undefined
            ? undefined
            : Math.min(viewport.rightPx, maximumRight);
        const bottomPx = viewport?.bottomPx === undefined
            ? undefined
            : Math.min(viewport.bottomPx, Math.max(0, window.innerHeight - 80));
        const effectiveRight = rightPx ?? (isSticky ? 30 : (mediaQuery.matches ? 23 : 30));
        const popupOffset = compactMobile ? Math.max(0, 16 - effectiveRight) : 25;
        if (this.config.launcher)
            this.popup.style.right = `${popupOffset}px`;
        const wrapperStyles = {
            position: positionMode,
            right: isSticky ? '0px' : (rightPx === undefined ? currentOffset : `${rightPx}px`),
            bottom: bottomPx === undefined ? (isSticky ? desktopOffset : currentOffset) : `${bottomPx}px`,
            display: 'flex',
            alignItems: 'flex-end',
            flexDirection: 'column',
            gap: compactMobile ? '16px' : '48px',
            width: 'fit-content',
            maxWidth: this.config.launcher ? `calc(100vw - ${effectiveRight + popupOffset + 16}px)` : 'calc(100vw - 32px)',
            boxSizing: 'border-box',
            zIndex: String(this.config.zIndex)
        };
        Object.assign(this.popupWrapper.style, wrapperStyles);
        if (isSticky) {
            const rightMargin = rightPx === undefined ? desktopOffset : `${rightPx}px`;
            this.popupWrapper.style.margin = `0 ${rightMargin} 0 0`;
            this.popupWrapper.style.marginRight = rightMargin;
            this.popupWrapper.style.marginLeft = 'auto';
        }
        else {
            const marginValue = mediaQuery.matches ? '8px' : '0';
            this.popupWrapper.style.margin = marginValue;
            this.popupWrapper.style.marginRight = marginValue;
            this.popupWrapper.style.marginLeft = marginValue;
            if (rightPx !== undefined)
                this.popupWrapper.style.marginRight = '0';
            if (bottomPx !== undefined)
                this.popupWrapper.style.marginBottom = '0';
        }
    }
    setLauncherSuppressed(suppressed) {
        this.popupWrapper.setAttribute('data-shine-modal-open', suppressed ? 'true' : 'false');
        this.popupWrapper.style.display = suppressed ? 'none' : (this.inlineLauncher ? 'inline-flex' : 'flex');
        this.aiLogo.style.display = suppressed ? 'none' : 'flex';
        this.aiLogo.setAttribute('aria-hidden', suppressed ? 'true' : 'false');
        this.popupWrapper.style.pointerEvents = suppressed ? 'none' : '';
        if (!suppressed)
            this.constrainFloatingPosition();
    }
    stylePixel(key, fallback) {
        const value = this.config.styling?.[key];
        return value === undefined ? fallback : `${value}px`;
    }
    hasPopupSizing() {
        const styling = this.config.styling;
        return styling?.popupMaxWidthPx !== undefined || styling?.popupPaddingPx !== undefined
            || styling?.popupFontSizePx !== undefined || styling?.closeButtonSizePx !== undefined;
    }
    minimumPopupTextHeight() {
        const fontSize = this.config.styling?.popupFontSizePx;
        return fontSize === undefined ? (this.isCompactMobileLayout() ? 22 : 21) : fontSize * 1.5;
    }
    setupPopup() {
        this.popup.classList.add('popup');
        const compactMobile = this.isCompactMobileLayout();
        Object.assign(this.popup.style, {
            position: 'relative',
            display: 'flex',
            gap: compactMobile ? '8px' : '16px',
            bottom: compactMobile ? '0' : '-25px',
            right: compactMobile ? (this.config.launcher ? this.popup.style.right : '0') : '25px',
            maxWidth: this.config.launcher ? `min(${compactMobile ? 277 : 306}px, 100%)` : (compactMobile ? '277px' : '306px'),
            boxSizing: this.config.launcher ? 'border-box' : '',
            padding: this.stylePixel('popupPaddingPx', compactMobile ? '16px' : '24px'),
            backgroundColor: this.config.colors.backgroundColor,
            borderRadius: this.stylePixel('popupBorderRadiusPx', compactMobile ? '8px' : '10px'),
            boxShadow: this.config.colors.popupShadow,
            cursor: 'pointer'
        });
        if (this.hasPopupSizing()) {
            const padding = this.config.styling?.popupPaddingPx ?? (compactMobile ? 16 : 24);
            const closeSize = this.config.styling?.closeButtonSizePx ?? 32;
            this.popup.style.paddingTop = `${Math.max(padding, closeSize / 2 + 8)}px`;
            const width = this.config.styling?.popupMaxWidthPx ?? (compactMobile ? 277 : 306);
            this.popup.style.maxWidth = `min(${width}px, 100%, calc(100vw - 64px))`;
            this.popup.style.boxSizing = 'border-box';
        }
        this.popup.setAttribute('aria-hidden', 'false');
    }
    setupAIIcon() {
        const aiIcon = document.createElement('div');
        aiIcon.classList.add('ai-icon');
        Object.assign(aiIcon.style, {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px'
        });
        aiIcon.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="20" viewBox="0 0 15 20" fill="none">
                <path d="M8.32223 7.80688C8.45973 7.39438 9.04224 7.39438 9.17974 7.80688L9.98598 10.2281C10.1635 10.7604 10.4624 11.244 10.8593 11.6406C11.2561 12.0372 11.7399 12.336 12.2722 12.5131L14.6922 13.3194C15.1047 13.4569 15.1047 14.0394 14.6922 14.1769L12.271 14.9831C11.7387 15.1606 11.2551 15.4596 10.8585 15.8564C10.4619 16.2533 10.1632 16.737 9.98598 17.2694L9.17974 19.6894C9.15009 19.7797 9.09268 19.8584 9.01568 19.9141C8.93869 19.9699 8.84605 19.9999 8.75098 19.9999C8.65592 19.9999 8.56328 19.9699 8.48629 19.9141C8.40929 19.8584 8.35188 19.7797 8.32223 19.6894L7.51598 17.2681C7.33865 16.736 7.03984 16.2525 6.64323 15.8559C6.24662 15.4593 5.7631 15.1605 5.23098 14.9831L2.80973 14.1769C2.71941 14.1472 2.64075 14.0898 2.58499 14.0128C2.52922 13.9358 2.4992 13.8432 2.4992 13.7481C2.4992 13.6531 2.52922 13.5604 2.58499 13.4834C2.64075 13.4064 2.71941 13.349 2.80973 13.3194L5.23098 12.5131C5.7631 12.3358 6.24662 12.037 6.64323 11.6404C7.03984 11.2438 7.33865 10.7602 7.51598 10.2281L8.32223 7.80688ZM3.49348 1.43313C3.51141 1.379 3.54594 1.33189 3.59216 1.2985C3.63839 1.26511 3.69396 1.24714 3.75098 1.24714C3.80801 1.24714 3.86358 1.26511 3.9098 1.2985C3.95603 1.33189 3.99056 1.379 4.00848 1.43313L4.49223 2.88563C4.70848 3.53313 5.21598 4.04063 5.86348 4.25688L7.31599 4.74063C7.37012 4.75855 7.41722 4.79308 7.45061 4.83931C7.484 4.88553 7.50197 4.94111 7.50197 4.99813C7.50197 5.05515 7.484 5.11072 7.45061 5.15695C7.41722 5.20318 7.37012 5.2377 7.31599 5.25563L5.86348 5.73938C5.54395 5.84543 5.2536 6.02462 5.01553 6.26268C4.77747 6.50074 4.59828 6.7911 4.49223 7.11063L4.00848 8.56313C3.99056 8.61726 3.95603 8.66437 3.9098 8.69775C3.86358 8.73114 3.80801 8.74911 3.75098 8.74911C3.69396 8.74911 3.63839 8.73114 3.59216 8.69775C3.54594 8.66437 3.51141 8.61726 3.49348 8.56313L3.00973 7.11063C2.90369 6.7911 2.7245 6.50074 2.48644 6.26268C2.24837 6.02462 1.95802 5.84543 1.63848 5.73938L0.185984 5.25563C0.131852 5.2377 0.0847455 5.20318 0.051358 5.15695C0.0179704 5.11072 0 5.05515 0 4.99813C0 4.94111 0.0179704 4.88553 0.051358 4.83931C0.0847455 4.79308 0.131852 4.75855 0.185984 4.74063L1.63848 4.25688C1.95802 4.15083 2.24837 3.97164 2.48644 3.73358C2.7245 3.49552 2.90369 3.20516 3.00973 2.88563L3.49348 1.43313ZM12.3297 0.121878C12.3421 0.0862862 12.3652 0.0554232 12.3959 0.0335803C12.4266 0.0117374 12.4633 0 12.501 0C12.5387 0 12.5754 0.0117374 12.6061 0.0335803C12.6368 0.0554232 12.6599 0.0862862 12.6722 0.121878L12.9947 1.08938C13.1385 1.52188 13.4772 1.86063 13.9097 2.00438L14.8772 2.32688C14.9128 2.33922 14.9437 2.36234 14.9655 2.39303C14.9874 2.42372 14.9991 2.46046 14.9991 2.49813C14.9991 2.5358 14.9874 2.57253 14.9655 2.60322C14.9437 2.63392 14.9128 2.65704 14.8772 2.66938L13.9097 2.99188C13.6968 3.06313 13.5033 3.18286 13.3445 3.34165C13.1857 3.50043 13.066 3.69393 12.9947 3.90688L12.6722 4.87438C12.6599 4.90997 12.6368 4.94083 12.6061 4.96268C12.5754 4.98452 12.5387 4.99626 12.501 4.99626C12.4633 4.99626 12.4266 4.98452 12.3959 4.96268C12.3652 4.94083 12.3421 4.90997 12.3297 4.87438L12.0072 3.90688C11.936 3.69393 11.8162 3.50043 11.6575 3.34165C11.4987 3.18286 11.3052 3.06313 11.0922 2.99188L10.126 2.66938C10.0904 2.65704 10.0595 2.63392 10.0377 2.60322C10.0158 2.57253 10.0041 2.5358 10.0041 2.49813C10.0041 2.46046 10.0158 2.42372 10.0377 2.39303C10.0595 2.36234 10.0904 2.33922 10.126 2.32688L11.0935 2.00438C11.526 1.86063 11.8647 1.52188 12.0085 1.08938L12.3297 0.121878Z" fill=" ${this.config.colors.textColor}"/>
            </svg>
        `;
        this.popup.appendChild(aiIcon);
    }
    setupText() {
        this.textElement = document.createElement('div');
        this.textElement.classList.add('text');
        const compactMobile = this.isCompactMobileLayout();
        Object.assign(this.textElement.style, {
            color: this.config.colors.textColor,
            fontFamily: compactMobile ? 'Inter, sans-serif' : 'Open Sans, sans-serif',
            fontSize: this.stylePixel('popupFontSizePx', '14px'),
            lineHeight: this.config.styling?.popupFontSizePx === undefined
                ? (compactMobile ? '22px' : '21px') : '1.5',
            zIndex: "3003"
        });
        this.updateText(this.currentPrompt?.text ?? this.config.text.aiMessage);
        if (this.config.launcher || this.hasPopupSizing()) {
            this.textElement.style.minWidth = '0';
            this.textElement.style.overflowWrap = 'anywhere';
            this.textElement.tabIndex = 0;
            this.textElement.setAttribute('role', 'region');
            this.textElement.setAttribute('aria-label', 'Chat invitation');
        }
        this.popup.appendChild(this.textElement);
    }
    setupCloseButton() {
        this.closeButton.type = 'button';
        this.closeButton.setAttribute('aria-label', 'Close widget');
        this.closeButton.classList.add('close');
        this.closeButton.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M2.146 2.146a.5.5 0 0 1 .708 0L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 1 1-.708-.708L7.293 8 2.146 2.854a.5.5 0 0 1 0-.708z"/>
            </svg>
    `;
        const compactMobile = this.isCompactMobileLayout();
        Object.assign(this.closeButton.style, {
            position: 'absolute',
            top: this.config.styling?.closeButtonSizePx === undefined
                ? (compactMobile ? '-16px' : '-15px') : `-${this.config.styling.closeButtonSizePx / 2}px`,
            right: this.config.styling?.closeButtonSizePx === undefined
                ? (compactMobile ? '-16px' : '-15px') : '0px',
            bottom: 'auto',
            width: this.stylePixel('closeButtonSizePx', '32px'),
            height: this.stylePixel('closeButtonSizePx', '32px'),
            borderRadius: this.stylePixel('closeButtonBorderRadiusPx', '50%'),
            padding: '0',
            boxSizing: 'border-box',
            border: 'none',
            backgroundColor: compactMobile ? '#121413' : this.config.colors.closeButtonBackground,
            color: compactMobile ? '#ffffff' : this.config.colors.closeButtonColor,
            boxShadow: this.config.colors.closeButtonShadow,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: '3004',
            margin: '0'
        });
        this.popup.appendChild(this.closeButton);
    }
    setupAILogo(useFallback = false) {
        this.aiLogo.replaceChildren();
        this.launcherImageReady = false;
        this.launcherImageFailed = useFallback;
        this.aiLogo.classList.add('ai-logo');
        const compactMobile = this.isCompactMobileLayout();
        const rectangularLauncher = this.config.launcherShape === 'rectangle';
        this.aiLogo.classList.toggle('ai-logo--rectangle', rectangularLauncher);
        Object.assign(this.aiLogo.style, {
            width: rectangularLauncher ? '106px' : '64px',
            height: rectangularLauncher ? '69px' : '64px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: this.stylePixel('launcherBorderRadiusPx', rectangularLauncher ? '16px' : '100%'),
            backgroundColor: this.config.colors.launcherBackground,
            boxShadow: compactMobile ? 'none' : this.config.colors.launcherShadow,
            boxSizing: 'border-box',
            padding: this.config.images.launcherLogoPadding || '0px',
            overflow: 'hidden'
        });
        this.aiLogo.style.border = '0';
        this.aiLogo.style.margin = '0';
        if (this.config.images.aiLogo1 && !useFallback) {
            const aiLogoImg = document.createElement('img');
            aiLogoImg.alt = '';
            Object.assign(aiLogoImg.style, {
                width: rectangularLauncher ? 'auto' : '100%',
                height: rectangularLauncher ? 'auto' : '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transition: 'opacity 0.5s ease-in-out'
            });
            this.applyLauncherIntrinsicSize(aiLogoImg);
            if (this.config.launcher) {
                const onLoad = () => {
                    if (aiLogoImg.naturalWidth <= 0 || aiLogoImg.naturalHeight <= 0) {
                        this.restoreLauncherFallback();
                        return;
                    }
                    this.launcherImageReady = true;
                    this.applyLauncherLayout();
                    this.constrainFloatingPosition();
                };
                aiLogoImg.addEventListener('load', onLoad);
                aiLogoImg.addEventListener('error', () => this.restoreLauncherFallback());
                aiLogoImg.src = this.config.images.aiLogo1;
                this.aiLogo.appendChild(aiLogoImg);
                if (aiLogoImg.complete && aiLogoImg.naturalWidth > 0)
                    onLoad();
            }
            else {
                aiLogoImg.src = this.config.images.aiLogo1;
                this.aiLogo.appendChild(aiLogoImg);
            }
        }
        else {
            const defaultSvg = "<svg width=\"64\" height=\"64\" viewBox=\"0 0 64 64\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\">\n" +
                "<path d=\"M32.9784 28.0524C33.2259 27.3099 34.2743 27.3099 34.5218 28.0524L35.9728 32.4107C36.2922 33.3688 36.8304 34.2392 37.5446 34.9532C38.2588 35.6671 39.1295 36.2048 40.0877 36.5237L44.4432 37.975C45.1856 38.2225 45.1856 39.271 44.4432 39.5185L40.0854 40.9697C39.1274 41.2892 38.2571 41.8274 37.5432 42.5417C36.8294 43.256 36.2917 44.1268 35.9728 45.085L34.5218 49.441C34.4684 49.6036 34.3651 49.7452 34.2265 49.8456C34.0879 49.946 33.9212 50 33.7501 50C33.579 50 33.4123 49.946 33.2737 49.8456C33.1351 49.7452 33.0318 49.6036 32.9784 49.441L31.5273 45.0828C31.2081 44.1249 30.6703 43.2546 29.9565 42.5407C29.2427 41.8268 28.3725 41.289 27.4148 40.9697L23.057 39.5185C22.8944 39.4651 22.7528 39.3618 22.6525 39.2232C22.5521 39.0846 22.4981 38.9179 22.4981 38.7467C22.4981 38.5756 22.5521 38.4089 22.6525 38.2703C22.7528 38.1317 22.8944 38.0283 23.057 37.975L27.4148 36.5237C28.3725 36.2045 29.2427 35.6667 29.9565 34.9528C30.6703 34.2389 31.2081 33.3685 31.5273 32.4107L32.9784 28.0524ZM40.1911 14.2194C40.2133 14.1553 40.255 14.0998 40.3102 14.0604C40.3654 14.0211 40.4316 14 40.4994 14C40.5672 14 40.6333 14.0211 40.6885 14.0604C40.7437 14.0998 40.7854 14.1553 40.8076 14.2194L41.388 15.9609C41.6467 16.7394 42.2564 17.3491 43.0348 17.6079L44.7761 18.1884C44.8402 18.2106 44.8958 18.2522 44.9351 18.3075C44.9744 18.3627 44.9955 18.4288 44.9955 18.4966C44.9955 18.5645 44.9744 18.6306 44.9351 18.6858C44.8958 18.7411 44.8402 18.7827 44.7761 18.8049L43.0348 19.3854C42.6516 19.5137 42.3033 19.7292 42.0175 20.015C41.7317 20.3008 41.5162 20.6491 41.388 21.0324L40.8076 22.7739C40.7854 22.838 40.7437 22.8935 40.6885 22.9329C40.6333 22.9722 40.5672 22.9933 40.4994 22.9933C40.4316 22.9933 40.3654 22.9722 40.3102 22.9329C40.255 22.8935 40.2133 22.838 40.1911 22.7739L39.6107 21.0324C39.4825 20.6491 39.267 20.3008 38.9812 20.015C38.6954 19.7292 38.3472 19.5137 37.9639 19.3854L36.2248 18.8049C36.1608 18.7827 36.1052 18.7411 36.0659 18.6858C36.0266 18.6306 36.0055 18.5645 36.0055 18.4966C36.0055 18.4288 36.0266 18.3627 36.0659 18.3075C36.1052 18.2522 36.1608 18.2106 36.2248 18.1884L37.9661 17.6079C38.7445 17.3491 39.3542 16.7394 39.6129 15.9609L40.1911 14.2194Z\" fill=\"white\"/>\n" +
                "<path d=\"M32.9784 28.0524C33.2259 27.3099 34.2743 27.3099 34.5218 28.0524L35.9728 32.4107C36.2922 33.3688 36.8304 34.2392 37.5446 34.9532C38.2588 35.6671 39.1295 36.2048 40.0877 36.5237L44.4432 37.975C45.1856 38.2225 45.1856 39.271 44.4432 39.5185L40.0854 40.9697C39.1274 41.2892 38.2571 41.8274 37.5432 42.5417C36.8294 43.256 36.2917 44.1268 35.9728 45.085L34.5218 49.441C34.4684 49.6036 34.3651 49.7452 34.2265 49.8456C34.0879 49.946 33.9212 50 33.7501 50C33.579 50 33.4123 49.946 33.2737 49.8456C33.1351 49.7452 33.0318 49.6036 32.9784 49.441L31.5273 45.0828C31.2081 44.1249 30.6703 43.2546 29.9565 42.5407C29.2427 41.8268 28.3725 41.289 27.4148 40.9697L23.057 39.5185C22.8944 39.4651 22.7528 39.3618 22.6525 39.2232C22.5521 39.0846 22.4981 38.9179 22.4981 38.7467C22.4981 38.5756 22.5521 38.4089 22.6525 38.2703C22.7528 38.1317 22.8944 38.0283 23.057 37.975L27.4148 36.5237C28.3725 36.2045 29.2427 35.6667 29.9565 34.9528C30.6703 34.2389 31.2081 33.3685 31.5273 32.4107L32.9784 28.0524ZM24.2876 16.5796C24.3199 16.4822 24.382 16.3974 24.4652 16.3373C24.5484 16.2772 24.6484 16.2449 24.751 16.2449C24.8537 16.2449 24.9537 16.2772 25.0369 16.3373C25.1201 16.3974 25.1822 16.4822 25.2145 16.5796L26.0852 19.1942C26.4744 20.3597 27.3878 21.2732 28.5531 21.6624L31.1674 22.5332C31.2648 22.5654 31.3496 22.6276 31.4097 22.7108C31.4698 22.794 31.5021 22.894 31.5021 22.9967C31.5021 23.0993 31.4698 23.1993 31.4097 23.2825C31.3496 23.3658 31.2648 23.4279 31.1674 23.4602L28.5531 24.3309C27.978 24.5218 27.4555 24.8444 27.027 25.2729C26.5985 25.7014 26.276 26.224 26.0852 26.7992L25.2145 29.4137C25.1822 29.5111 25.1201 29.5959 25.0369 29.656C24.9537 29.7161 24.8537 29.7485 24.751 29.7485C24.6484 29.7485 24.5484 29.7161 24.4652 29.656C24.382 29.5959 24.3199 29.5111 24.2876 29.4137L23.4169 26.7992C23.2261 26.224 22.9036 25.7014 22.4751 25.2729C22.0466 24.8444 21.524 24.5218 20.949 24.3309L18.3347 23.4602C18.2373 23.4279 18.1525 23.3658 18.0924 23.2825C18.0323 23.1993 18 23.0993 18 22.9967C18 22.894 18.0323 22.794 18.0924 22.7108C18.1525 22.6276 18.2373 22.5654 18.3347 22.5332L20.949 21.6624C21.524 21.4715 22.0466 21.149 22.4751 20.7205C22.9036 20.292 23.2261 19.7693 23.4169 19.1942L24.2876 16.5796ZM40.1911 14.2194C40.2133 14.1553 40.255 14.0998 40.3102 14.0604C40.3654 14.0211 40.4316 14 40.4994 14C40.5672 14 40.6333 14.0211 40.6885 14.0604C40.7437 14.0998 40.7854 14.1553 40.8076 14.2194L41.388 15.9609C41.6467 16.7394 42.2564 17.3491 43.0348 17.6079L44.7761 18.1884C44.8402 18.2106 44.8958 18.2522 44.9351 18.3075C44.9744 18.3627 44.9955 18.4288 44.9955 18.4966C44.9955 18.5645 44.9744 18.6306 44.9351 18.6858C44.8958 18.7411 44.8402 18.7827 44.7761 18.8049L43.0348 19.3854C42.6516 19.5137 42.3033 19.7292 42.0175 20.015C41.7317 20.3008 41.5162 20.6491 41.388 21.0324L40.8076 22.7739C40.7854 22.838 40.7437 22.8935 40.6885 22.9329C40.6333 22.9722 40.5672 22.9933 40.4994 22.9933C40.4316 22.9933 40.3654 22.9722 40.3102 22.9329C40.255 22.8935 40.2133 22.838 40.1911 22.7739L39.6107 21.0324C39.4825 20.6491 39.267 20.3008 38.9812 20.015C38.6954 19.7292 38.3472 19.5137 37.9639 19.3854L36.2248 18.8049C36.1608 18.7827 36.1052 18.7411 36.0659 18.6858C36.0266 18.6306 36.0055 18.5645 36.0055 18.4966C36.0055 18.4288 36.0266 18.3627 36.0659 18.3075C36.1052 18.2522 36.1608 18.2106 36.2248 18.1884L37.9661 17.6079C38.7445 17.3491 39.3542 16.7394 39.6129 15.9609L40.1911 14.2194Z\" fill=\"white\"/>\n" +
                "</svg>";
            this.aiLogo.innerHTML = defaultSvg;
            if (useFallback) {
                this.aiLogo.querySelectorAll('path').forEach(path => path.setAttribute('fill', this.config.colors.textColor));
            }
        }
    }
    restoreLauncherFallback() {
        if (this.logoSwapTimer !== undefined) {
            clearInterval(this.logoSwapTimer);
            this.logoSwapTimer = undefined;
        }
        this.setupAILogo(true);
        this.applyLauncherLayout();
        if (this.popupWrapper.getAttribute('data-shine-modal-open') === 'true')
            this.setLauncherSuppressed(true);
        else
            this.constrainFloatingPosition();
    }
    applyLauncherIntrinsicSize(image) {
        const devicePixelRatio = typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio)
            ? window.devicePixelRatio
            : 1;
        const intrinsicSize = Math.max(64, Math.round(64 * Math.min(Math.max(devicePixelRatio, 1), 3)));
        image.setAttribute('width', `${intrinsicSize}`);
        image.setAttribute('height', `${intrinsicSize}`);
    }
    getLauncherViewport() {
        return window.matchMedia('(max-width: 768px)').matches
            ? this.config.launcher?.mobile
            : this.config.launcher?.desktop;
    }
    applyLauncherLayout() {
        if (!this.config.launcher)
            return;
        const viewport = this.getLauncherViewport();
        const image = this.aiLogo.querySelector('img');
        const imageOnly = this.config.launcher.renderMode === 'image' && image !== null && this.launcherImageReady;
        const rectangle = this.config.launcherShape === 'rectangle';
        const bottom = this.inlineLauncher ? 0 : parseFloat(this.popupWrapper.style.bottom) || 0;
        const teaserReserve = this.inlineLauncher ? 0 : (this.isCompactMobileLayout() ? 112 : 176);
        const styledPopupReserve = !this.inlineLauncher && this.hasPopupSizing()
            ? (parseFloat(this.popup.style.paddingTop) || 0)
                + (parseFloat(this.popup.style.paddingBottom) || 0)
                + (parseFloat(this.popupWrapper.style.gap) || 0)
                + Math.max(0, -parseFloat(this.closeButton.style.top) || 0) + this.minimumPopupTextHeight() + 32
            : 0;
        const maximumHeight = `max(16px, calc(100dvh - ${bottom + Math.max(teaserReserve + 16, styledPopupReserve)}px))`;
        Object.assign(this.aiLogo.style, {
            width: viewport?.widthPx === undefined ? (imageOnly ? 'auto' : (rectangle ? '106px' : '64px')) : `${viewport.widthPx}px`,
            height: viewport?.heightPx === undefined ? (imageOnly ? 'auto' : (rectangle ? '69px' : '64px')) : `${viewport.heightPx}px`,
            maxWidth: '100%',
            maxHeight: maximumHeight,
            minWidth: '0',
            padding: imageOnly ? '0px' : this.config.images.launcherLogoPadding,
            borderRadius: imageOnly ? '0px' : this.stylePixel('launcherBorderRadiusPx', rectangle ? '16px' : '100%'),
            backgroundColor: imageOnly ? 'transparent' : (this.launcherImageFailed ? this.config.colors.backgroundColor : this.config.colors.launcherBackground),
            boxShadow: imageOnly || this.isCompactMobileLayout() ? 'none' : this.config.colors.launcherShadow,
            overflow: imageOnly ? 'visible' : 'hidden',
        });
        if (imageOnly && image) {
            // Omit the DPR-sized HTML attributes so an unscaled asset keeps its actual aspect ratio.
            image.removeAttribute('width');
            image.removeAttribute('height');
            Object.assign(image.style, {
                display: 'block',
                width: viewport?.widthPx === undefined ? 'auto' : '100%',
                height: viewport?.heightPx === undefined ? 'auto' : '100%',
                maxWidth: '100%',
                maxHeight: maximumHeight,
            });
        }
    }
    constrainFloatingPosition() {
        if ((!this.config.launcher && !this.hasPopupSizing()) || this.inlineLauncher || !this.popupWrapper.isConnected)
            return;
        if (this.popupWrapper.getBoundingClientRect().height <= 0)
            return;
        if (this.textElement && this.popup.getAttribute('aria-hidden') !== 'true') {
            const popupStyle = getComputedStyle(this.popup);
            const padding = (parseFloat(popupStyle.paddingTop) || 0) + (parseFloat(popupStyle.paddingBottom) || 0);
            const gap = parseFloat(this.popupWrapper.style.gap) || 0;
            const closeOverhang = this.hasPopupSizing() ? Math.max(0, -parseFloat(this.closeButton.style.top) || 0) : 0;
            const availableTextHeight = window.innerHeight - this.aiLogo.getBoundingClientRect().height - gap - padding - closeOverhang - 32;
            this.textElement.style.maxHeight = `${Math.max(this.minimumPopupTextHeight(), availableTextHeight)}px`;
            this.textElement.style.overflowY = 'auto';
        }
        const wrapperTop = this.popupWrapper.getBoundingClientRect().top;
        const closeTop = this.closeButton.isConnected && this.popup.getAttribute('aria-hidden') !== 'true'
            ? this.closeButton.getBoundingClientRect().top
            : wrapperTop;
        const top = Math.min(wrapperTop, closeTop);
        if (top < 16) {
            const bottom = parseFloat(this.popupWrapper.style.bottom) || 0;
            this.popupWrapper.style.bottom = `${Math.max(0, bottom - (16 - top))}px`;
        }
    }
    appendElements() {
        if (!this.inlineLauncher) {
            this.popupWrapper.appendChild(this.popup);
        }
        this.popupWrapper.appendChild(this.aiLogo);
        this.mountTarget.appendChild(this.popupWrapper);
    }
    addEventListeners() {
        // Preload on hover/pointer enter to reduce first-open delay
        const hasWindow = typeof window !== 'undefined';
        const hasMatchMedia = hasWindow && typeof window.matchMedia === 'function';
        const hasTouchInput = (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) ||
            (hasWindow && typeof window.ontouchstart !== 'undefined');
        const supportsDesktopHover = hasMatchMedia
            ? window.matchMedia('(hover: hover) and (pointer: fine)').matches
            : !hasTouchInput;
        const hoverTargets = this.inlineLauncher ? [this.aiLogo] : [this.popup, this.aiLogo];
        const scrollPassthroughTargets = [this.popupWrapper, ...hoverTargets]
            .filter((el, index, arr) => el && arr.indexOf(el) === index);
        const hoverRevealEnabled = this.config.hoverRevealEnabled !== false;
        const allowHoverReveal = supportsDesktopHover && hoverRevealEnabled;
        hoverTargets.forEach(el => {
            if (supportsDesktopHover) {
                el.addEventListener('pointerenter', () => this.triggerPreload());
                el.addEventListener('mouseenter', () => this.triggerPreload());
            }
            el.addEventListener('touchstart', () => this.triggerPreload(), { passive: true });
        });
        if (hasTouchInput) {
            this.setupTouchScrollPassthrough(scrollPassthroughTargets);
        }
        this.setupWheelScrollPassthrough(scrollPassthroughTargets);
        if (!this.inlineLauncher) {
            this.popup.addEventListener('click', () => this.openModal());
            this.closeButton.addEventListener('click', (e) => {
                e.stopPropagation();
                this.closePopup();
            });
        }
        this.aiLogo.addEventListener('click', () => this.openModal(!this.inlineLauncher));
        const revealOnHover = () => this.showPopupIfClosed();
        const hideOnHover = () => this.hidePopupIfClosed();
        if (allowHoverReveal && !this.inlineLauncher) {
            this.aiLogo.addEventListener('pointerenter', revealOnHover);
            this.aiLogo.addEventListener('mouseenter', revealOnHover);
            this.popupWrapper.addEventListener('pointerleave', hideOnHover);
            this.popupWrapper.addEventListener('mouseleave', hideOnHover);
            this.popupWrapper.addEventListener('focusin', revealOnHover);
            this.popupWrapper.addEventListener('focusout', (event) => {
                const next = (event.relatedTarget || document.activeElement);
                if (next && this.popupWrapper.contains(next)) {
                    return;
                }
                hideOnHover();
            });
        }
    }
    openForPreview() {
        return this.openModal(false);
    }
    async openModal(reportPromptClick = true) {
        freezeWidgetLocale();
        if (reportPromptClick) {
            this.reportPromptClick();
        }
        try {
            ensureMaterialSymbols();
        }
        catch {
            /* best-effort icon preload */
        }
        this.refreshWidgetRefs();
        if (this.showModalIfAvailable()) {
            void this.ensureWidgetReady(1800);
            return;
        }
        try {
            await this.ensureWidgetMounted();
        }
        catch {
            return;
        }
        if (this.showModalIfAvailable()) {
            void this.ensureWidgetReady(1800);
            return;
        }
        await this.ensureWidgetReady(1800);
        this.showModalIfAvailable();
    }
    showModalIfAvailable() {
        this.refreshWidgetRefs();
        if (!this.modal)
            return false;
        this.modal.style.display = 'flex';
        this.setLauncherSuppressed(true);
        if (!this.inlineLauncher) {
            document.cookie = 'shine-popup-closed=true; max-age=2592000; path=/';
            this.closePopup();
        }
        return true;
    }
    closeModal() {
        if (this.modal) {
            this.modal.style.display = 'none';
            this.setLauncherSuppressed(false);
        }
        else {
            console.error("Modal element with ID 'shineModal' not found.");
        }
    }
    closePopup() {
        if (this.isPopupClosed)
            return;
        this.isPopupClosed = true;
        this.popup.classList.add('fade-out');
        window.setTimeout(() => {
            this.popup.classList.remove('fade-out');
            this.togglePopupHidden(true);
        }, 500);
        document.cookie = 'shine-popup-closed=true; max-age=2592000; path=/';
    }
    swapImages() {
        const aiLogoImg = this.aiLogo.querySelector('img');
        if (!aiLogoImg)
            return;
        this.logoSwapTimer = setInterval(() => {
            this.isLogo1 = !this.isLogo1;
            if (this.config.launcher?.renderMode === 'image')
                this.launcherImageReady = false;
            aiLogoImg.src = this.isLogo1 ? this.config.images.aiLogo1 : this.config.images.aiLogo2;
            this.applyLauncherLayout();
            this.constrainFloatingPosition();
        }, 2000);
    }
    applyStyles() {
        const style = document.createElement('style');
        style.textContent = `
      .popup {
        --popup-translate-value: 0px;
        transition: opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease;
        transform: translateY(var(--popup-translate-value));
        touch-action: manipulation;
      }
      .popup-hidden {
        --popup-translate-value: 6px;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        display:none !important;
      }
      .ai-logo {
        touch-action: manipulation;
      }
      .popup:before, .popup:after {
        transition: opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease;
        content: "";
        bottom: -30px;
        right: 20px;
        position: absolute;
        width: 40px;
        height: 31px;
        border-radius: 0 38px 18px 0;
        box-shadow: 16px 20px 0px 15px ${this.config.colors.backgroundColor};
        transform: rotate(170deg) translateY(var(--popup-translate-value));
        z-index: -2;
      }
      .popup:after {
        filter: none;
        z-index: 2;
      }
      .popup-hidden:before,
      .popup-hidden:after {
        opacity: 0;
        visibility: hidden;
        display:none !important;
      }
      .shine-popup-compact-mobile .popup:before,
      .shine-popup-compact-mobile .popup:after,
      .shine-popup-no-tail .popup:before,
      .shine-popup-no-tail .popup:after {
        display: none !important;
      }
      .fade-out {
        opacity: 0;
        transition: opacity 0.5s ease-out;
      }
      @media (max-width: 768px) {
        .popup { max-width: 90%; }
        .ai-logo { max-width: calc(100vw - 32px); }
        .ai-logo:not(.ai-logo--rectangle) { width: 48px; height: 48px; }
      }
    `;
        document.head.appendChild(style);
    }
    handleInitialDisplay() {
        const device = this.getDeviceType();
        if (device === 'mobile' && this.config.mobilePopupOnce && this.hasMobilePopupBeenSeen()) {
            this.isPopupClosed = true;
            this.togglePopupHidden(true);
            return;
        }
        if (device === 'mobile' && this.config.mobilePopupOnce) {
            this.markMobilePopupSeen();
        }
        if (this.shouldAutoCloseInitialPopup(device)) {
            this.closeButton.remove();
            window.setTimeout(() => this.closePopup(), this.config.autoClose?.delayMs ?? DEFAULT_AUTO_CLOSE_DELAY_MS);
        }
    }
    setupScrollReveal() {
        const scrollReveal = this.config.scrollReveal;
        if (!scrollReveal?.enabled) {
            this.debugScrollReveal('disabled');
            return;
        }
        if (this.shouldSuppressMobilePopupAfterPriorView()) {
            this.debugScrollReveal('mobile_once_seen');
            return;
        }
        if (!this.isScrollRevealDeviceEnabled(scrollReveal.devices)) {
            this.debugScrollReveal('device_disabled', { device: this.getDeviceType(), devices: scrollReveal.devices });
            return;
        }
        const threshold = this.normalizeScrollThreshold(scrollReveal.thresholdPx);
        const onScroll = () => {
            if (!this.scrollRevealLoggedListening) {
                this.scrollRevealLoggedListening = true;
                this.debugScrollReveal('listening', {
                    thresholdPx: threshold,
                    device: this.getDeviceType(),
                    isPopupClosed: this.isPopupClosed
                });
            }
            if (this.scrollRevealTriggered)
                return;
            if (!this.isPopupClosed)
                return;
            const scrollY = this.getScrollPosition();
            if (scrollY < threshold)
                return;
            if (!this.scrollRevealLoggedThreshold) {
                this.scrollRevealLoggedThreshold = true;
                this.debugScrollReveal('threshold_reached', { scrollY, thresholdPx: threshold });
            }
            this.scrollRevealTriggered = true;
            this.isPopupClosed = false;
            this.togglePopupHidden(false);
        };
        if (typeof window !== 'undefined') {
            window.addEventListener('scroll', onScroll, { passive: true });
            this.debugScrollReveal('listener_attached', {
                thresholdPx: threshold,
                device: this.getDeviceType(),
                isPopupClosed: this.isPopupClosed
            });
        }
    }
    normalizeScrollThreshold(value) {
        if (typeof value !== 'number' || Number.isNaN(value)) {
            return 800;
        }
        return Math.max(0, value);
    }
    isScrollRevealDeviceEnabled(devices) {
        if (!devices)
            return true;
        const device = this.getDeviceType();
        const enabled = device === 'mobile'
            ? devices.mobile
            : device === 'tablet'
                ? devices.tablet
                : devices.desktop;
        return typeof enabled === 'boolean' ? enabled : true;
    }
    getDeviceType() {
        if (typeof window === 'undefined' || typeof document === 'undefined')
            return 'desktop';
        const width = window.innerWidth || document.documentElement?.clientWidth || 0;
        if (width <= 768)
            return 'mobile';
        if (width <= 1024)
            return 'tablet';
        return 'desktop';
    }
    normalizeAutoClose(autoClose) {
        const configuredDelay = autoClose?.delayMs;
        const delayMs = typeof configuredDelay === 'number' && Number.isFinite(configuredDelay)
            ? Math.max(0, configuredDelay)
            : DEFAULT_AUTO_CLOSE_DELAY_MS;
        return {
            enabled: autoClose?.enabled ?? true,
            delayMs,
            devices: {
                desktop: autoClose?.devices?.desktop ?? false,
                tablet: autoClose?.devices?.tablet ?? false,
                mobile: autoClose?.devices?.mobile ?? true,
            },
            closeOnSameClientReferrer: autoClose?.closeOnSameClientReferrer ?? true,
        };
    }
    shouldAutoCloseInitialPopup(device) {
        const autoClose = this.config.autoClose ?? this.normalizeAutoClose(undefined);
        if (!autoClose.enabled)
            return false;
        const deviceEnabled = device === 'mobile'
            ? autoClose.devices.mobile
            : device === 'tablet'
                ? autoClose.devices.tablet
                : autoClose.devices.desktop;
        return deviceEnabled || (autoClose.closeOnSameClientReferrer && this.hasSameClientReferrer());
    }
    hasSameClientReferrer() {
        try {
            const client = window.shineSettings?.client ?? '';
            return client.length > 0 && document.referrer.includes(client);
        }
        catch {
            return false;
        }
    }
    shouldSuppressMobilePopupAfterPriorView() {
        return this.getDeviceType() === 'mobile'
            && this.config.mobilePopupOnce === true
            && this.hasMobilePopupBeenSeen();
    }
    getMobilePopupSeenStorageKey() {
        const client = window.shineSettings?.client || 'unknown-client';
        const chatbot = window.shineSettings?.chatbot || 'unknown-chatbot';
        return `${MOBILE_POPUP_SEEN_PREFIX}:${client}:${chatbot}`;
    }
    hasMobilePopupBeenSeen() {
        try {
            return window.localStorage.getItem(this.getMobilePopupSeenStorageKey()) === 'true';
        }
        catch {
            return false;
        }
    }
    markMobilePopupSeen() {
        try {
            window.localStorage.setItem(this.getMobilePopupSeenStorageKey(), 'true');
        }
        catch {
            /* localStorage can be disabled; mobile-once then degrades to per-page display */
        }
    }
    getScrollPosition() {
        let docEl = 0;
        let body = 0;
        let win = 0;
        let page = 0;
        if (typeof document !== 'undefined') {
            if (document.documentElement && typeof document.documentElement.scrollTop === 'number') {
                docEl = document.documentElement.scrollTop;
            }
            if (document.body && typeof document.body.scrollTop === 'number') {
                body = document.body.scrollTop;
            }
        }
        if (typeof window !== 'undefined') {
            if (typeof window.scrollY === 'number') {
                win = window.scrollY;
            }
            if (typeof window.pageYOffset === 'number') {
                page = window.pageYOffset;
            }
        }
        return Math.max(docEl, body, win, page);
    }
    debugScrollReveal(event, data) {
        try {
            if (!window?.shineSettings?.debug)
                return;
        }
        catch {
            return;
        }
        const payload = data ? ` ${JSON.stringify(data)}` : '';
        console.log(`[shine-widget][scroll-reveal] ${event}${payload}`);
    }
    showPopupIfClosed() {
        if (!this.isPopupClosed)
            return;
        this.togglePopupHidden(false);
    }
    hidePopupIfClosed() {
        if (!this.isPopupClosed)
            return;
        this.togglePopupHidden(true);
    }
    canScrollPopupText(target, deltaY) {
        const text = this.textElement;
        if (!text || !(target instanceof Node) || !text.contains(target))
            return false;
        return deltaY < 0 ? text.scrollTop > 0
            : deltaY > 0 && text.scrollTop + text.clientHeight < text.scrollHeight;
    }
    setupTouchScrollPassthrough(targets) {
        if (typeof window === 'undefined') {
            return;
        }
        const getScrollPosition = () => {
            if (typeof document !== 'undefined') {
                if (document.documentElement && typeof document.documentElement.scrollTop === 'number') {
                    return document.documentElement.scrollTop;
                }
                if (document.body && typeof document.body.scrollTop === 'number') {
                    return document.body.scrollTop;
                }
            }
            if (typeof window.scrollY === 'number') {
                return window.scrollY;
            }
            if (typeof window.pageYOffset === 'number') {
                return window.pageYOffset;
            }
            return 0;
        };
        const schedule = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : ((cb) => window.setTimeout(() => cb(Date.now()), 16));
        let lastTouchY = null;
        let lastKnownScrollY = getScrollPosition();
        const handleTouchStart = (event) => {
            if (event.touches.length !== 1)
                return;
            lastTouchY = event.touches[0].clientY;
            lastKnownScrollY = getScrollPosition();
        };
        const handleTouchMove = (event) => {
            if (event.touches.length !== 1 || lastTouchY === null)
                return;
            const currentY = event.touches[0].clientY;
            const deltaY = lastTouchY - currentY;
            if (Math.abs(deltaY) < 0.5) {
                lastTouchY = currentY;
                return;
            }
            if (this.canScrollPopupText(event.target, deltaY)) {
                lastTouchY = currentY;
                lastKnownScrollY = getScrollPosition();
                return;
            }
            schedule(() => {
                const currentScrollY = getScrollPosition();
                const scrollDelta = currentScrollY - lastKnownScrollY;
                if (Math.abs(scrollDelta) < Math.abs(deltaY) * 0.6 && typeof window.scrollBy === 'function') {
                    try {
                        window.scrollBy(0, deltaY);
                    }
                    catch {
                        /* scroll forwarding best-effort */
                    }
                }
                lastKnownScrollY = getScrollPosition();
            });
            lastTouchY = currentY;
        };
        const resetTouchTracking = () => {
            lastTouchY = null;
        };
        targets.forEach(target => {
            target.addEventListener('touchstart', handleTouchStart, { passive: true });
            target.addEventListener('touchmove', handleTouchMove, { passive: true });
            target.addEventListener('touchend', resetTouchTracking, { passive: true });
            target.addEventListener('touchcancel', resetTouchTracking, { passive: true });
        });
    }
    setupWheelScrollPassthrough(targets) {
        if (typeof window === 'undefined' || typeof document === 'undefined' || typeof WheelEvent !== 'function') {
            return;
        }
        const uniqueTargets = targets
            .filter((target, index, arr) => target && arr.indexOf(target) === index);
        if (!uniqueTargets.length)
            return;
        const disablePointerEventsTemporarily = () => {
            const previous = [];
            uniqueTargets.forEach(target => {
                previous.push([target, target.style.pointerEvents]);
                target.style.pointerEvents = 'none';
            });
            return () => {
                previous.forEach(([target, value]) => {
                    target.style.pointerEvents = value;
                });
            };
        };
        const cloneWheelEventInit = (event) => ({
            deltaX: event.deltaX,
            deltaY: event.deltaY,
            deltaZ: event.deltaZ,
            deltaMode: event.deltaMode,
            clientX: event.clientX,
            clientY: event.clientY,
            screenX: event.screenX,
            screenY: event.screenY,
            ctrlKey: event.ctrlKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey,
            metaKey: event.metaKey,
            bubbles: true,
            cancelable: true,
            composed: true
        });
        const forwardWheel = (event) => {
            if (this.canScrollPopupText(event.target, event.deltaY))
                return;
            let forwardTarget = null;
            const restorePointerEvents = disablePointerEventsTemporarily();
            try {
                if (typeof document.elementFromPoint === 'function') {
                    forwardTarget = document.elementFromPoint(event.clientX, event.clientY);
                }
            }
            catch {
                forwardTarget = null;
            }
            finally {
                restorePointerEvents();
            }
            if (!forwardTarget || uniqueTargets.includes(forwardTarget)) {
                forwardTarget = document.body || document.documentElement || window;
            }
            try {
                const forwardedEvent = new WheelEvent(event.type, cloneWheelEventInit(event));
                forwardTarget.dispatchEvent(forwardedEvent);
            }
            catch {
                try {
                    window.scrollBy({ top: event.deltaY, left: event.deltaX, behavior: 'auto' });
                }
                catch {
                    /* wheel forwarding best-effort */
                }
            }
            if (event.cancelable) {
                event.preventDefault();
                event.stopPropagation();
            }
        };
        uniqueTargets.forEach(target => {
            target.addEventListener('wheel', forwardWheel, { passive: false });
        });
    }
    getPositionMode() {
        const configured = typeof this.config.positionMode === 'string'
            ? this.config.positionMode.trim().toLowerCase()
            : '';
        return configured === 'sticky' ? 'sticky' : 'fixed';
    }
    togglePopupHidden(hidden) {
        if (hidden) {
            this.popup.style.display = 'none';
            this.popup.classList.add('popup-hidden');
            this.popup.setAttribute('aria-hidden', 'true');
        }
        else {
            this.popup.style.display = 'flex';
            this.popup.classList.remove('popup-hidden');
            this.popup.setAttribute('aria-hidden', 'false');
            this.constrainFloatingPosition();
        }
    }
    // ---- Preloading helpers ----
    triggerPreload() {
        if (this.preloadInProgress)
            return;
        this.preloadInProgress = true;
        freezeWidgetLocale();
        // Ensure icon stylesheet is requested as early as possible
        try {
            ensureMaterialSymbols();
        }
        catch {
            /* best-effort icon preload */
        }
        this.ensureWidgetReady(2000).finally(() => {
            this.preloadInProgress = false;
        });
    }
    async ensureWidgetReady(timeoutMs) {
        try {
            if (!this.widgetRuntimeMounted && this.ensureWidgetRuntime) {
                await this.ensureWidgetMounted();
            }
            else {
                this.refreshWidgetRefs();
            }
            const widget = document.querySelector('shine-widget-component');
            if (!widget)
                return;
            if (typeof widget.preload === 'function') {
                try {
                    await widget.preload();
                }
                catch {
                    /* widget preload is best-effort */
                }
            }
            if (typeof widget.whenFrontendReady === 'function') {
                try {
                    await widget.whenFrontendReady(timeoutMs);
                }
                catch {
                    /* readiness wait is best-effort */
                }
            }
        }
        catch {
            /* widget lookup failed; ignore */
        }
    }
    async ensureWidgetMounted() {
        if (this.widgetRuntimeMounted || !this.ensureWidgetRuntime) {
            this.refreshWidgetRefs();
            return;
        }
        if (this.ensureWidgetRuntime && !this.widgetReadyPromise) {
            this.widgetReadyPromise = this.ensureWidgetRuntime().finally(() => {
                this.widgetReadyPromise = null;
            });
        }
        if (this.widgetReadyPromise) {
            await this.widgetReadyPromise;
        }
        this.widgetRuntimeMounted = true;
        this.refreshWidgetRefs();
    }
    updateText(message) {
        if (!this.textElement)
            return;
        this.textElement.innerHTML = DOMPurify.sanitize(message, {
            ALLOWED_TAGS: ['strong', 'em', 'u', 'b', 'i', 'br'],
            ALLOWED_ATTR: []
        });
        this.constrainFloatingPosition();
    }
    applyDynamicPrompt(response) {
        if (!response.prompt?.text)
            return;
        this.currentPrompt = response.prompt;
        this.updateText(response.prompt.text);
    }
    setClickReporter(reporter) {
        this.clickReporter = reporter;
    }
    safeCssColor(value, fallback) {
        const text = `${value ?? ''}`.trim();
        if (!text || /[;{}]/.test(text))
            return fallback;
        const cssApi = typeof CSS !== 'undefined' ? CSS : null;
        if (cssApi?.supports?.('color', text) === false)
            return fallback;
        return text;
    }
    safeCssShadow(value, fallback) {
        const text = `${value ?? ''}`.trim();
        if (!text)
            return fallback;
        if (text.toLowerCase() === 'none')
            return 'none';
        if (text.length > 240 || /[;{}<>]/.test(text))
            return fallback;
        if (!/^[#(),./\s%a-zA-Z0-9-]+$/.test(text))
            return fallback;
        const cssApi = typeof CSS !== 'undefined' ? CSS : null;
        if (cssApi?.supports?.('box-shadow', text) === false)
            return fallback;
        return text;
    }
    safeCssLength(value, fallback) {
        const text = `${value ?? ''}`.trim();
        if (!text || text.length > 80 || /[;{}<>]/.test(text))
            return fallback;
        const cssApi = typeof CSS !== 'undefined' ? CSS : null;
        if (cssApi?.supports?.('padding', text) === false)
            return fallback;
        return text;
    }
    safeImageUrl(value) {
        const text = `${value ?? ''}`.trim();
        if (!text)
            return '';
        if (/^data:image\/(?:png|jpe?g|gif|webp|svg\+xml);base64,[a-z0-9+/=\s]+$/i.test(text)) {
            return text;
        }
        try {
            const url = new URL(text, window.location.href);
            return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
        }
        catch {
            return '';
        }
    }
    reportPromptClick() {
        if (!this.currentPrompt || !this.clickReporter)
            return;
        if (this.popup.getAttribute('aria-hidden') === 'true')
            return;
        try {
            this.clickReporter(this.currentPrompt);
        }
        catch {
            /* click reporting is best-effort */
        }
    }
}
